'use client';

import { create } from 'zustand';
import { getAuthScopeRevision, registerAuthScopedCacheReset } from '@/lib/auth-scoped-state-reset';

import { socialPublishingApi } from '@/lib/api/social-publishing';
import { API_BASE_URL } from '@/lib/api/base-url';
import type { ConsumerSocialPublishPlatform, SocialPlatformAccount } from '@/types/social-publishing';

type ConnectionStatus = Partial<Record<ConsumerSocialPublishPlatform, { configured: boolean }>>;

interface SocialOAuthMessage {
  type: 'dobidobi:social-oauth';
  success: boolean;
  platform: ConsumerSocialPublishPlatform;
  account?: SocialPlatformAccount;
  error?: string;
  errorCode?: string;
  referenceId?: string;
}

const SOCIAL_OAUTH_POPUP_TIMEOUT_MS = 2 * 60 * 1000;

type SocialConnectionError = Error & { code?: string; referenceId?: string; platform?: ConsumerSocialPublishPlatform };

function createSocialConnectionError(code: string, message: string): SocialConnectionError {
  const error = new Error(message) as SocialConnectionError;
  error.code = code;
  return error;
}

interface SocialAccountsState {
  accounts: SocialPlatformAccount[];
  connectionStatus: ConnectionStatus | null;
  isLoading: boolean;
  pendingPlatform: ConsumerSocialPublishPlatform | null;
  disconnectingId: string | null;
  error: string | null;
  connectionError: SocialConnectionError | null;
  fetchAccounts: () => Promise<void>;
  connect: (platform: ConsumerSocialPublishPlatform, returnContext?: Record<string, unknown>) => Promise<void>;
  disconnect: (accountId: string) => Promise<void>;
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}

function waitForOAuthPopup(
  popup: Window,
  platform: ConsumerSocialPublishPlatform,
): Promise<SocialOAuthMessage> {
  return new Promise((resolve, reject) => {
    let settled = false;
    function cleanup() {
      window.clearTimeout(timeoutId);
      window.clearInterval(closePollId);
      window.removeEventListener('message', onMessage);
      window.removeEventListener('offline', onOffline);
    }
    function finish(callback: () => void) {
      if (settled) return;
      settled = true;
      cleanup();
      callback();
    }
    function onOffline() {
      finish(() => reject(new Error('SOCIAL_OAUTH_NETWORK_OFFLINE')));
    }
    function onMessage(event: MessageEvent) {
      if (event.source !== popup) return;
      if (event.origin !== new URL(API_BASE_URL, window.location.origin).origin) return;
      const payload = event.data as Partial<SocialOAuthMessage> | null;
      if (!payload || payload.type !== 'dobidobi:social-oauth' || payload.platform !== platform) return;
      if (!payload.success) {
        const error = createSocialConnectionError(payload.errorCode || 'SOCIAL_OAUTH_FAILED', payload.error || 'SOCIAL_OAUTH_FAILED');
        if (typeof payload.referenceId === 'string' && /^[a-f0-9]{12}$/.test(payload.referenceId)) {
          error.referenceId = payload.referenceId;
        }
        finish(() => reject(error));
        return;
      }
      finish(() => resolve(payload as SocialOAuthMessage));
    }
    const timeoutId = window.setTimeout(() => {
      finish(() => reject(new Error('SOCIAL_OAUTH_TIMEOUT')));
    }, SOCIAL_OAUTH_POPUP_TIMEOUT_MS);
    const closePollId = window.setInterval(() => {
      if (popup.closed) {
        finish(() => reject(new Error('SOCIAL_OAUTH_CANCELLED')));
      }
    }, 500);
    window.addEventListener('message', onMessage);
    window.addEventListener('offline', onOffline);
  });
}

let listGeneration = 0;
let mutationGeneration = 0;

export const useSocialAccountsStore = create<SocialAccountsState>((set, get) => ({
  accounts: [],
  connectionStatus: null,
  isLoading: false,
  pendingPlatform: null,
  disconnectingId: null,
  error: null,
  connectionError: null,
  fetchAccounts: async () => {
    if (get().disconnectingId) return;
    const request = ++listGeneration;
    const scope = getAuthScopeRevision();
    set({ isLoading: true, error: null });
    try {
      const response = await socialPublishingApi.listAccounts();
      if (request !== listGeneration || scope !== getAuthScopeRevision()) return;
      if (response.error || !response.data) {
        throw createSocialConnectionError(
          response.error?.code || 'SOCIAL_ACCOUNTS_LOAD_FAILED',
          response.error?.message || 'SOCIAL_ACCOUNTS_LOAD_FAILED',
        );
      }
      set({
        accounts: Array.isArray(response.data.items) ? response.data.items : [],
        connectionStatus: response.data.connection_status?.platforms ?? {},
        isLoading: false,
      });
    } catch (error) {
      if (request !== listGeneration || scope !== getAuthScopeRevision()) return;
      set({ isLoading: false, error: apiErrorMessage(error, 'SOCIAL_ACCOUNTS_LOAD_FAILED') });
      throw error;
    }
  },
  connect: async (platform, returnContext) => {
    if (typeof window === 'undefined') return;
    if (get().pendingPlatform || get().disconnectingId) throw new Error('SOCIAL_OAUTH_PENDING');
    const scope = getAuthScopeRevision();
    const revision = ++mutationGeneration;
    if (get().connectionStatus?.[platform]?.configured === false) {
      throw new Error('SOCIAL_OAUTH_UNAVAILABLE');
    }
    const popup = window.open('', `dobidobi-social-${platform}`, 'popup,width=620,height=760,resizable=yes,scrollbars=yes');
    if (!popup) throw new Error('SOCIAL_OAUTH_POPUP_BLOCKED');
    popup.document.title = 'DOBIDOBI';
    set({ pendingPlatform: platform, error: null, connectionError: null });
    try {
      const response = await socialPublishingApi.connectConsumerAccount(platform, returnContext);
      if (response.error || !response.data?.authorization_url) {
        throw createSocialConnectionError(
          response.error?.code || 'SOCIAL_OAUTH_START_FAILED',
          response.error?.message || 'SOCIAL_OAUTH_START_FAILED',
        );
      }
      if (scope !== getAuthScopeRevision() || revision !== mutationGeneration) throw createSocialConnectionError('SOCIAL_OAUTH_SESSION_EXPIRED', 'SOCIAL_OAUTH_SESSION_EXPIRED');
      popup.location.replace(response.data.authorization_url);
      await waitForOAuthPopup(popup, platform);
      if (scope !== getAuthScopeRevision() || revision !== mutationGeneration) throw createSocialConnectionError('SOCIAL_OAUTH_SESSION_EXPIRED', 'SOCIAL_OAUTH_SESSION_EXPIRED');
      await get().fetchAccounts();
      if (scope !== getAuthScopeRevision() || revision !== mutationGeneration) throw createSocialConnectionError('SOCIAL_OAUTH_SESSION_EXPIRED', 'SOCIAL_OAUTH_SESSION_EXPIRED');
      if (!get().accounts.some((account) => account.platform === platform && account.auth_status === 'authorized')) {
        throw new Error('SOCIAL_OAUTH_ACCOUNT_NOT_FOUND');
      }
    } catch (error) {
      const failure: SocialConnectionError = error instanceof Error ? error : new Error('SOCIAL_OAUTH_FAILED');
      failure.platform = platform;
      if (scope === getAuthScopeRevision() && revision === mutationGeneration) set({ connectionError: failure });
      try {
        if (!popup.closed) popup.close();
      } catch {
        // The provider may have left the popup in a cross-origin state.
      }
      throw error;
    } finally {
      if (scope === getAuthScopeRevision() && revision === mutationGeneration) set({ pendingPlatform: null });
    }
  },
  disconnect: async (accountId) => {
    if (get().pendingPlatform || get().disconnectingId) throw new Error('SOCIAL_ACCOUNT_MUTATION_PENDING');
    const scope = getAuthScopeRevision();
    const revision = ++mutationGeneration;
    listGeneration += 1;
    set({ isLoading: true, disconnectingId: accountId, error: null, connectionError: null });
    try {
      const response = await socialPublishingApi.disconnectAccount(accountId);
      if (scope !== getAuthScopeRevision() || revision !== mutationGeneration) return;
      if (response.error || !response.data) {
        throw new Error(response.error?.message || 'SOCIAL_ACCOUNT_DISCONNECT_FAILED');
      }
      set((state) => ({
        accounts: state.accounts.filter((item) => item.id !== accountId),
        isLoading: false,
      }));
    } catch (error) {
      if (scope !== getAuthScopeRevision() || revision !== mutationGeneration) return;
      set({ isLoading: false });
      throw error;
    } finally {
      if (scope === getAuthScopeRevision() && revision === mutationGeneration) set({ disconnectingId: null });
    }
  },
}));

registerAuthScopedCacheReset('social-accounts', () => {
  listGeneration += 1;
  mutationGeneration += 1;
  useSocialAccountsStore.setState({ accounts: [], connectionStatus: null, isLoading: false, pendingPlatform: null, disconnectingId: null, error: null, connectionError: null });
});
